import { Grid2, Typography } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import cn from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Button from "@mui/material/Button";
import { InlineWidget } from "react-calendly";
import DoneIcon from "@mui/icons-material/Done";
import { Elements } from "@stripe/react-stripe-js";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { useTranslate } from "../../../../hooks/useTranslate";
import { useStudents } from "../../../../context/students";
import Students1 from "../../../../assets/images/images/students3.jpg";
import Students2 from "../../../../assets/images/images/students4.jpg";
import Preloader from "../../../../components/Preloader";
import axiosManager from "../../../../services/axiosManager";
import SummaryBox from "../../../../components/SummaryBox";
import { formatCurrency } from "../../../../utils/formatCurrency";
import TruncatedText from "../../../../components/TruncatedText";
import useStatuses from "../../../../context/status/useStatuses";
import { useToast } from "../../../../context/toast";
import EmptyContent from "../../../../components/EmptyContent";
import { stripeAppearance, stripeFonts, stripePromise } from "../../../../services/stripeConfig";
import CheckoutForm from "../../../applicant/TuitionBilling/components/CheckoutForm";

export const ExamSupport = () => {
    const { t } = useTranslate();
    const navigate = useNavigate();
    const toast = useToast();
    const [searchParams] = useSearchParams();
    const hasPaymentComplete = searchParams.has("payment-complete");

    const { contact } = useStatuses();
    const { activeStudentId } = useStudents();

    const [assessmentIdSelected, setAssessmentIdSelected] = useState(null);
    const [isBooked, setIsBooked] = useState(false);

    const showAllAssessments = !assessmentIdSelected && !hasPaymentComplete;
    const showAssessmentBooking = !!assessmentIdSelected && !isBooked;
    const showAssessmentPayment = !!assessmentIdSelected && isBooked;

    const { isFetching: isLoading, data: assessments } = useQuery({
        queryKey: ["exams", activeStudentId],
        queryFn: () => axiosManager.get$(`/parent/api/students/${activeStudentId}/assessments?type=exams`).then((response) => response?.data),
        initialData: [],
        retry: 0,
    });
    const bookingUrl = (assessments || []).find((it) => it.assessmentId === assessmentIdSelected)?.bookingUrl;

    const { mutate: setAssessmentAsBooked, isPending: isSettingCall, data: assessmentPayment } = useMutation({
        mutationFn: () => axiosManager.post$(`/parent/api/students/${activeStudentId}/assessments/${assessmentIdSelected}`, {}).then((response) => response?.data),
        onError: () => toast.error(t("common.errorMessage")),
    });
    const stripeOptions = useMemo(() => ({
        clientSecret: assessmentPayment?.clientSecret,
        appearance: stripeAppearance,
        fonts: stripeFonts,
        loader: "auto",
    }), [assessmentPayment?.clientSecret]);

    useEffect(() => {
        const handleCalendlyEvent = (event) => {
            if (event.data.event === "calendly.event_scheduled") {
                setIsBooked(true);
                setAssessmentAsBooked();
            }
        };

        window.addEventListener("message", handleCalendlyEvent);

        return () => {
            window.removeEventListener("message", handleCalendlyEvent);
        };
    }, [setAssessmentAsBooked]);

    return (
        <div className="page-content">
            <div className="student-head-section" />
            <Grid2 container spacing={2} className="min-head-section-height--small">
                <Grid2 size={{ xs: 12, md: 7, lg: 8 }} className="text-white">
                    <Breadcrumbs
                        breadcrumbs={[
                            { label: t("navigation.home"), href: `/home/${activeStudentId}` },
                            { label: t("examsInformation.title"), href: `/exams-information/${activeStudentId}` },
                            { label: t("examSupport.title"), current: true },
                        ]}
                    />
                    <Typography variant="h1" className="text-white">
                        {t("examSupport.title")}
                    </Typography>
                    <Typography variant="body1" className="mt-4 text-white" component="div">
                        {assessmentIdSelected ? t("examSupport.subtitleCalendly") : t("examSupport.subtitle")}
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 5, lg: 4 }}>
                    <div className="images-head-section">
                        <div className="img-head-section">
                            <img alt="students-1" src={Students1} />
                        </div>
                        <div className="img-head-section">
                            <img alt="students-2" src={Students2} />
                        </div>
                    </div>
                </Grid2>
            </Grid2>
            <div className="relative z-1">
                {hasPaymentComplete && (
                    <SummaryBox
                        isFormBox
                        className="mt-8"
                        contentClassName="flex flex-column align-center gap-2"
                    >
                        <Preloader isLoading={isSettingCall}>
                            <span className="status-done-icon mb-4">
                                <DoneIcon />
                            </span>
                            <Typography variant="h4" component="div" className="text-primary">
                                {t("examSupport.thankYouForBooking")}
                            </Typography>
                            <Button
                                variant="contained"
                                className="button-accent mt-4"
                                onClick={() => navigate(`/exams-information/exam-support/${activeStudentId}`)}
                            >
                                {t("examSupport.bookAnother")}
                            </Button>
                        </Preloader>
                    </SummaryBox>
                )}
                {showAssessmentPayment && (
                    <Preloader isLoading={isSettingCall}>
                        <Elements
                            options={stripeOptions}
                            stripe={stripePromise}
                        >
                            <CheckoutForm invoice={assessmentPayment?.invoice || {}} isInvoicePay />
                        </Elements>
                    </Preloader>
                )}
                {showAssessmentBooking && (
                    <>
                        <Typography variant="h2" className="text-white">
                            {t("examSupport.selectYourAvailability")}
                        </Typography>
                        <EmptyContent data={bookingUrl} text={t("examSupport.noCalendlyUrl")} className="mt-4">
                            <InlineWidget
                                url={bookingUrl}
                                style={{ minWidth: "320px", height: "700px" }}
                                prefill={{
                                    name: `${contact?.forename || ""} ${contact?.surname || ""}`,
                                    email: contact?.emailAddress,
                                }}
                            />
                        </EmptyContent>
                    </>
                )}
                {showAllAssessments && (
                    <Preloader isLoading={isLoading}>
                        <Typography variant="h2" className="text-white">
                            {t("examSupport.availableAssessments")}
                        </Typography>
                        <EmptyContent data={assessments} text={t("examSupport.noAssessments")} className="mt-4">
                            {(assessments || []).map((assessment) => (
                                <SummaryBox
                                    isFormBox
                                    key={assessment.assessmentId}
                                    className="mt-8"
                                >
                                    <div className="flex xs:flex-column align-center gap-4 mb-4">
                                        <Typography variant="h4" component="div" className="text-primary">
                                            {assessment.name}
                                        </Typography>
                                        <span className="badge-primary">
                                            {formatCurrency("GBP").format(assessment.price)}
                                        </span>
                                    </div>
                                    <TruncatedText>
                                        {assessment.description}
                                    </TruncatedText>
                                    <div className="divider divider-right mt-6 mb-4">
                                        <Typography variant="h4" component="span" className="text-primary">
                                            {t("examSupport.availableTopicsInclude")}
                                        </Typography>
                                    </div>
                                    <div className="flex flex-wrap align-center gap-3 mb-8">
                                        <div className="chip-tag">{t("examSupport.availableTopic1")}</div>
                                        <div className="chip-tag">{t("examSupport.availableTopic2")}</div>
                                        <div className="chip-tag">{t("examSupport.availableTopic3")}</div>
                                        <div className="chip-tag">{t("examSupport.availableTopic4")}</div>
                                        <div className="chip-tag">{t("examSupport.availableTopic5")}</div>
                                        <div className="chip-tag">{t("examSupport.availableTopic6")}</div>
                                        <div className="chip-tag">{t("examSupport.availableTopic7")}</div>
                                        <div className="chip-tag">{t("examSupport.availableTopic8")}</div>
                                    </div>
                                    <Button
                                        variant="contained"
                                        className="w-280 button-accent shrink-0"
                                        onClick={() => setAssessmentIdSelected(assessment.assessmentId)}
                                    >
                                        {t("examSupport.bookNow")}
                                    </Button>
                                </SummaryBox>
                            ))}
                        </EmptyContent>
                    </Preloader>
                )}
            </div>
        </div>
    );
};
